import { useEffect, useRef, useState } from "react";
import {
  Briefcase,
  
  ChevronDown,
  ClipboardList,
  Flag,
  LogIn,
  LogOut,
  Mail,
  Menu,
  MessageCircle,
  PlusCircle,
  Search,
  Shield,
} from "lucide-react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { languages, useLanguage } from "@/lib/realEstateI18n";
import { useWhatsApp } from "@/lib/siteSettings";
import { useAuth } from "@/lib/auth";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

type NavItem = { to: string; labelKey: string; icon: React.ComponentType<{ className?: string }> };

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-full px-4 py-2 text-sm font-semibold transition ${
    isActive
      ? "bg-primary text-primary-foreground shadow-premium"
      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
  }`;

const triggerClass = (active: boolean) =>
  `inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold transition ${
    active
      ? "bg-primary text-primary-foreground shadow-premium"
      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
  }`;

function DesktopDropdown({
  label,
  items,
  paths,
}: {
  label: string;
  items: NavItem[];
  paths: string[];
}) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const timer = useRef<number | undefined>(undefined);
  const active = paths.some((p) => location.pathname === p);
  const { t } = useTranslation();

  const onEnter = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), 150);
  };
  const onLeave = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={triggerClass(active)}
      >
        {label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute start-0 top-full z-50 mt-2 min-w-[240px] animate-fade-in rounded-lg border border-primary/20 bg-background/95 p-2 shadow-lg backdrop-blur"
        >
          {items.map((item) => {
            const isActive = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={`flex w-full items-center gap-3 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors duration-150 ${
                  isActive
                    ? "bg-muted/40 text-primary"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`}
                role="menuitem"
              >
                <Icon className="h-4 w-4 shrink-0 opacity-80" />
                <span>{t(item.labelKey)}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MobileGroup({
  label,
  items,
  paths,
  onNavigate,
}: {
  label: string;
  items: NavItem[];
  paths: string[];
  onNavigate: () => void;
}) {
  const location = useLocation();
  const { t } = useTranslation();
  const groupActive = paths.some((p) => location.pathname === p);
  const [open, setOpen] = useState(groupActive);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className={`flex min-h-[48px] w-full items-center justify-between rounded-lg px-4 py-3 text-base font-bold transition ${
          groupActive
            ? "border-s-4 border-primary bg-muted/50 text-foreground"
            : "text-foreground hover:bg-muted/40"
        }`}
      >
        <span>{label}</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden transition-all">
        <div className="ms-3 flex flex-col gap-1 border-s border-border/60 ps-3 py-1">
          {items.map((item) => {
            const isActive = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                className={`flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold transition ${
                  isActive
                    ? "bg-muted/40 text-primary"
                    : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0 opacity-80" />
                <span>{t(item.labelKey)}</span>
              </Link>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function SimpleMobileLink({
  to,
  label,
  onNavigate,
}: {
  to: string;
  label: string;
  onNavigate: () => void;
}) {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={`flex min-h-[48px] items-center rounded-lg px-4 py-3 text-base font-bold transition ${
        isActive
          ? "border-s-4 border-primary bg-muted/50 text-foreground"
          : "text-foreground hover:bg-muted/40"
      }`}
    >
      {label}
    </Link>
  );
}

export const RealEstateLayout = () => {
  const { language, setLanguage, dir } = useLanguage();
  const { t } = useTranslation();
  const { waUrl } = useWhatsApp();
  const { userId, isAdmin, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  const propertiesItems: NavItem[] = [
    { to: "/submit", labelKey: "nav.propertiesMenu.submit", icon: PlusCircle },
    { to: "/wanted", labelKey: "nav.propertiesMenu.wanted", icon: Search },
    { to: "/request", labelKey: "nav.propertiesMenu.request", icon: ClipboardList },
  ];
  const servicesItems: NavItem[] = [
    { to: "/services", labelKey: "nav.servicesMenu.overview", icon: Briefcase },
    { to: "/citizenship", labelKey: "nav.servicesMenu.citizenship", icon: Flag },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground" dir={dir}>
      <a href="#main-content" className="skip-link">{t("a11y.skipToContent")}</a>
      <header
        className="sticky top-0 z-50 min-h-[64px] bg-background shadow-soft"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-3 font-bold tracking-tight text-foreground" aria-label={t("brand")}>
            <img src="/icon.svg" alt="" aria-hidden="true" width={40} height={40} className="h-10 w-10" />
            <span className="hidden sm:inline">{t("brand")}</span>
          </Link>

          {/* Desktop nav: 4 tabs */}
          <div className="hidden items-center gap-1 rounded-full border border-border bg-card/75 p-1 md:flex">
            <NavLink to="/" className={navLinkClass} end>{t("nav.home")}</NavLink>
            <DesktopDropdown
              label={t("nav.properties")}
              items={propertiesItems}
              paths={propertiesItems.map((i) => i.to)}
            />
            <DesktopDropdown
              label={t("nav.services")}
              items={servicesItems}
              paths={servicesItems.map((i) => i.to)}
            />
            <NavLink to="/contact" className={navLinkClass}>{t("nav.contact")}</NavLink>
            {isAdmin && (
              <NavLink to="/admin" className={navLinkClass}>
                <span className="inline-flex items-center gap-1">
                  <Shield className="h-3.5 w-3.5" /> {t("nav.admin")}
                </span>
              </NavLink>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden rounded-full border border-border bg-card/80 p-1 md:flex" aria-label="Language switcher">
              {languages.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  onClick={() => setLanguage(item.code)}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold transition ${
                    language === item.code
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            {userId ? (
              <button
                onClick={() => void signOut()}
                className="hidden rounded-full border border-border bg-card/80 px-3 py-2 text-xs font-bold text-foreground transition hover:bg-secondary md:inline-flex items-center gap-1"
              >
                <LogOut className="h-3.5 w-3.5" /> {t("nav.signOut")}
              </button>
            ) : (
              <Link
                to="/auth"
                className="hidden rounded-full border border-border bg-card/80 px-3 py-2 text-xs font-bold text-foreground transition hover:bg-secondary md:inline-flex items-center gap-1"
              >
                <LogIn className="h-3.5 w-3.5" /> {t("nav.signIn")}
              </Link>
            )}

            {/* Mobile menu trigger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label={t("nav.menu")}
                  className="grid h-10 w-10 place-items-center rounded-full border border-border bg-card/80 text-foreground md:hidden"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side={dir === "rtl" ? "right" : "left"} className="w-[88vw] max-w-sm overflow-y-auto p-0">
                <div className="flex h-full flex-col">
                  <div className="flex items-center gap-3 border-b border-border px-5 py-4">
                    <img src="/icon.svg" alt="" aria-hidden="true" width={36} height={36} className="h-9 w-9" />
                    <span className="font-bold">{t("brand")}</span>
                  </div>
                  <div className="flex flex-1 flex-col gap-1 px-3 py-4">
                    <SimpleMobileLink to="/" label={t("nav.home")} onNavigate={closeMobile} />
                    <MobileGroup
                      label={t("nav.properties")}
                      items={propertiesItems}
                      paths={propertiesItems.map((i) => i.to)}
                      onNavigate={closeMobile}
                    />
                    <MobileGroup
                      label={t("nav.services")}
                      items={servicesItems}
                      paths={servicesItems.map((i) => i.to)}
                      onNavigate={closeMobile}
                    />
                    <SimpleMobileLink to="/contact" label={t("nav.contact")} onNavigate={closeMobile} />
                    {isAdmin && (
                      <SimpleMobileLink to="/admin" label={t("nav.admin")} onNavigate={closeMobile} />
                    )}
                  </div>
                  <div className="mt-auto border-t border-border px-5 py-4">
                    <div className="mb-3 flex rounded-full border border-border bg-card/80 p-1" aria-label="Language switcher">
                      {languages.map((item) => (
                        <button
                          key={item.code}
                          type="button"
                          onClick={() => setLanguage(item.code)}
                          className={`flex-1 rounded-full px-3 py-1.5 text-xs font-bold transition ${
                            language === item.code
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                    {userId ? (
                      <button
                        onClick={() => { void signOut(); closeMobile(); }}
                        className="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
                      >
                        <LogOut className="h-3.5 w-3.5" /> {t("nav.signOut")}
                      </button>
                    ) : (
                      <Link
                        to="/auth"
                        onClick={closeMobile}
                        className="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
                      >
                        <LogIn className="h-3.5 w-3.5" /> {t("nav.signIn")}
                      </Link>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </header>

      <main id="main-content" tabIndex={-1} className="animate-fade-in">
        <Outlet />
      </main>

      {/* Floating WhatsApp button (general contact only) */}
      <a
        href={waUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={t("footer.whatsapp")}
        className="fixed z-40 grid h-14 w-14 place-items-center rounded-full bg-success text-primary-foreground shadow-premium transition hover:scale-110"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)",
          insetInlineEnd: "calc(env(safe-area-inset-right, 0px) + 1.5rem)",
        }}
      >
        <MessageCircle className="h-7 w-7" />
      </a>

      <footer className="border-t border-border bg-surface">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 md:grid-cols-[1.5fr_1fr] lg:px-8">
          <div>
            <div className="mb-3 flex items-center gap-3 font-bold">
              <img src="/icon.svg" alt="" aria-hidden="true" width={36} height={36} className="h-9 w-9" />
              {t("brand")}
            </div>
            <p className="max-w-xl text-sm leading-7 text-muted-foreground">{t("footer.tagline")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 md:justify-end">
            <a className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground transition hover:scale-105" href={waUrl} target="_blank" rel="noreferrer">
              <MessageCircle className="h-4 w-4" /> {t("footer.whatsapp")}
            </a>
            <a className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-secondary" href="mailto:istanovaaa@gmail.com">
              <Mail className="h-4 w-4" /> {t("footer.email")}
            </a>
          </div>
        </div>
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-border/60 px-4 py-4 text-xs text-muted-foreground sm:px-6 lg:px-8">
          <Link to="/about" className="hover:text-foreground">{t("nav.about")}</Link>
          <Link to="/privacy" className="hover:text-foreground">{t("legal.privacy.title")}</Link>
          <Link to="/terms" className="hover:text-foreground">{t("legal.terms.title")}</Link>
          <span>© {new Date().getFullYear()} ISTANOVA</span>
        </div>
      </footer>
    </div>
  );
};
